'use client';

import { useTranslations } from 'next-intl';
import { ChevronRight, ExternalLink } from 'lucide-react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { Link } from '@/src/i18n/navigation';
import { LucideIcon } from '@/src/components/LucideIcon';
import { TrustSignal } from '@/src/components/features/property/TrustSignal';
import { sendEventToApi } from '@/src/components/features/property/lib/analytics';
import {
  emitTrackBuildingPayPerLinkClickEvent,
  emitTrackCostsAndFeesModalOpenEvent,
} from '@/src/analytics/hotpads/events';
import { VerifiedInline } from '@/src/components/features/listings/VerifiedInline';
import { ListingActionMenu } from '@/src/components/features/listings/ListingActionMenu';
import { TotalMonthlyPriceIndicator } from '@/src/components/features/listings/TotalMonthlyPrice';
import { type ListingActionAnalyticsContext, useListingActions } from '@/src/components/features/listing-actions';
import { Anchor, Heading } from '@zillow/constellation-10';
import { QuickFacts } from './sections/QuickFacts';
import { InlineCTAs } from './sections/InlineCTAs';
import { getListingSummaryDisplay } from './listingSummaryModule.utils';
import * as styles from './listingSummaryModule.styles';

type Props = {
  listing: PropertyDetailListing;
};

const ACTION_MENU_ANALYTICS: ListingActionAnalyticsContext = {
  triggerLocation: 'home_details_halo',
  triggerObject: 'listing_summary_module',
};

/**
 * HALO ListingSummaryModule (HDP).
 *
 * Hero summary at the top of the body: price + Total price/Fees-may-apply
 * indicator (shared with ListingCard via `TotalMonthlyPriceIndicator`) +
 * hide/report menu, beds/baths/sqft + Verified, building name + address,
 * website link, Costs calculator link,
 * units-available + see-floor-plans link, Quick Facts card, Message + Book
 * tour CTAs.
 *
 * Listing data lives as a prop; analytics dispatch via
 * `@/src/components/features/property/lib/analytics`.
 */
export const ListingSummaryModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const {
    shouldHideDetails,
    priceDisplay,
    showPrice,
    bedRangeDisplay,
    showBedRange,
    bathRangeDisplay,
    showBathRange,
    sqftDisplay,
    showSqft,
    showUnits,
    otherUnitsCount,
    buildingHref,
    showOtherUnitsLink,
    addressDisplay,
    buildingTitle,
    websiteUrl,
    websiteDisplayName,
  } = getListingSummaryDisplay(listing);

  const hideStreet = Boolean((listing.address as { hideStreet?: boolean } | undefined)?.hideStreet);
  const isVerified = Boolean((listing as unknown as { trusted?: boolean })?.trusted);
  const { listingHidden, toggleHide, openReport } = useListingActions(listing, ACTION_MENU_ANALYTICS);

  const handleWebsiteClick = () => {
    sendEventToApi('building_website_visited');
    if (listing.maloneLotIdEncoded) {
      sendEventToApi('building_website_clicked', listing.maloneLotIdEncoded);
    }
    emitTrackBuildingPayPerLinkClickEvent({
      listing,
      triggerLocation: 'home_details_halo',
      triggerObject: 'home_details_component|summary',
    });
  };

  const handleOpenCostsAndFees = () => {
    emitTrackCostsAndFeesModalOpenEvent({ listing });
    if (typeof document === 'undefined') return;
    const target = document.getElementById('cost-and-fees-module');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (shouldHideDetails) {
    return (
      <div className={styles.container}>
        {buildingTitle && (
          <Heading level={1} textStyle="heading-xs" className={styles.buildingNameLegacy}>
            {buildingTitle}
          </Heading>
        )}
      </div>
    );
  }

  const websiteAriaLabel =
    websiteDisplayName && !hideStreet
      ? t('visitWebsiteWithName', { name: websiteDisplayName })
      : t('visitPropertyWebsite');
  const renderCostsLink = () => {
    const linkContent = (
      <>
        <span className={styles.costsLinkText}>{t('costsCalculator')}</span>
        <LucideIcon icon={ChevronRight} size="sm" className={styles.costsLinkChevron} />
      </>
    );

    return (
      <button
        type="button"
        className={styles.costsLink}
        onClick={handleOpenCostsAndFees}
        aria-label={t('viewCostsAndFees')}
        data-testid="link-costs-fees"
      >
        {linkContent}
      </button>
    );
  };

  const showMetaRow = showBedRange || showBathRange || showSqft || isVerified;
  const metaItems = [
    showBedRange && (
      <span key="beds" data-testid="text-beds-row">
        {bedRangeDisplay}
      </span>
    ),
    showBathRange && (
      <span key="baths" data-testid="text-baths-row">
        {bathRangeDisplay}
      </span>
    ),
    showSqft && (
      <span key="sqft" data-testid="text-sqft-row">
        {sqftDisplay}
      </span>
    ),
  ].filter(Boolean);
  const showIdentityRow = Boolean(buildingTitle || addressDisplay);
  const identityHeadingLabel = [buildingTitle, addressDisplay].filter(Boolean).join(', ');

  const handleSeeFloorPlans = () => {
    if (typeof document === 'undefined') return;
    const target = document.getElementById('units-module');
    if (!target) return;

    const collapsedToggle = target.querySelector<HTMLButtonElement>(
      'button[aria-controls="units-module-details"][aria-expanded="false"]',
    );
    collapsedToggle?.click();

    const scrollToFloorPlans = () => target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (collapsedToggle && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(scrollToFloorPlans);
      return;
    }

    scrollToFloorPlans();
  };

  return (
    <div className={styles.container}>
      <div className={styles.heroStack} data-testid="cluster-price-decision">
        <div className={styles.leftColumn}>
          <div className={styles.identityGroup}>
            {showIdentityRow && (
              <Heading
                level={1}
                textStyle="heading-xs"
                className={styles.identityHeading}
                aria-label={identityHeadingLabel}
                data-testid="text-identity-line"
              >
                {(buildingTitle || (websiteUrl && !hideStreet)) && (
                  <span className={styles.identityNameRow}>
                    {buildingTitle && (
                      <span className={styles.identityBuildingName} data-testid="text-content-building-name">
                        {buildingTitle}
                      </span>
                    )}
                    {websiteUrl && !hideStreet && (
                      <Anchor
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={websiteAriaLabel}
                        className={styles.websiteLink}
                        onClick={handleWebsiteClick}
                        data-testid="link-visit-website"
                      >
                        <LucideIcon icon={ExternalLink} size="sm" />
                      </Anchor>
                    )}
                  </span>
                )}
                {addressDisplay && (
                  <span className={styles.identityAddress} data-testid="text-address-inline">
                    {addressDisplay}
                  </span>
                )}
              </Heading>
            )}

            <TrustSignal listing={listing} />
          </div>

          {showPrice && (
            <div className={styles.priceGroup}>
              <div className={styles.priceRow}>
                <span className={styles.priceInfo}>
                  <span className={styles.priceText} data-testid="text-price-range">
                    {`${priceDisplay}${t('perMo')}`}
                  </span>
                  <TotalMonthlyPriceIndicator
                    listing={listing}
                    testId="text-total-price"
                    feesTestId="text-fees-apply"
                  />
                </span>
                <ListingActionMenu
                  triggerId="listing-summary-action-menu"
                  listingHidden={listingHidden}
                  onHide={toggleHide}
                  onReport={openReport}
                  tone="neutral"
                  emphasis="bare"
                  size="sm"
                />
              </div>
              {renderCostsLink()}
            </div>
          )}

          {(showMetaRow || showUnits || showOtherUnitsLink) && (
            <div className={styles.detailsGroup}>
              {showMetaRow && (
                <div className={styles.metaRow} data-testid="row-unit-meta">
                  {metaItems.map((item, i) => (
                    <span key={i}>
                      {i > 0 && (
                        <span className={styles.metaSeparator} aria-hidden="true">
                          ·
                        </span>
                      )}
                      {item}
                    </span>
                  ))}
                  {isVerified && (
                    <>
                      {metaItems.length > 0 && (
                        <span className={styles.metaSeparator} aria-hidden="true">
                          ·
                        </span>
                      )}
                      <VerifiedInline data-testid="text-verified-inline" />
                    </>
                  )}
                </div>
              )}

              {showUnits && (
                <div className={styles.unitsRow}>
                  <button
                    type="button"
                    className={styles.seeFloorPlansLink}
                    onClick={handleSeeFloorPlans}
                    data-testid="link-see-floor-plans"
                  >
                    {t('seeFloorPlans')}
                  </button>
                </div>
              )}

              {showOtherUnitsLink && (
                <Link
                  href={buildingHref as Parameters<typeof Link>[0]['href']}
                  className={styles.otherUnitsLink}
                  data-testid="link-other-units-building"
                >
                  {t('otherRentalUnitsInBuilding', { count: otherUnitsCount })}
                </Link>
              )}
            </div>
          )}
        </div>

        <QuickFacts listing={listing} />
      </div>

      <InlineCTAs listing={listing} />
    </div>
  );
};
